import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
    activateProjectDomainAzure,
    addProjectDomain,
    checkProjectDomainDns,
    deleteProjectDomain,
    getProjectDomains,
    setProjectPrimaryDomain
} from "../../services/crmService";

import { isSuperAdminUser } from "../../services/authService";

function getDomainStatusClass(status = "") {
    const value = String(status).toLowerCase();

    if (value === "active") return "verified";
    if (value === "dnsverified") return "dns";
    if (value === "azurepending") return "azure";
    if (value === "failed") return "failed";
    if (value === "disabled") return "disabled";

    return "pending";
}

function formatDomainStatus(status = "") {
    const value = String(status || "");

    if (value === "PendingVerification") return "Pending Verification";
    if (value === "DnsVerified") return "DNS Verified";
    if (value === "AzurePending") return "Azure Pending";
    if (value === "Active") return "Active";
    if (value === "Failed") return "Failed";
    if (value === "Disabled") return "Disabled";

    return value || "Pending";
}

function getDomainLabels(hostName = "") {
    return String(hostName || "")
        .trim()
        .toLowerCase()
        .split(".")
        .filter(Boolean);
}

function getCommonDnsSubdomainName(hostName = "") {
    const labels = getDomainLabels(hostName);

    if (labels.length <= 2) {
        return "@";
    }

    return labels.slice(0, labels.length - 2).join(".");
}

function getCommonTxtHostName(hostName = "") {
    const subdomainName = getCommonDnsSubdomainName(hostName);

    if (!subdomainName || subdomainName === "@") {
        return "_shrianshnexus";
    }

    return `_shrianshnexus.${subdomainName}`;
}

function normalizeHostInput(value = "") {
    return String(value || "")
        .trim()
        .replace("https://", "")
        .replace("http://", "")
        .split("/")[0]
        .split(":")[0]
        .trim()
        .toLowerCase();
}

async function copyText(value, label) {
    try {
        await navigator.clipboard.writeText(value || "");
        toast.success(`${label} copied.`);
    } catch {
        toast.error(`Could not copy ${label}.`);
    }
}
export default function ProjectDomainsPanel({
    projectId,
    liveUrl,
    onChanged,
    onProjectChanged,
    embedded = false
}) {
    const [domains, setDomains] = useState([]);
    const [hostName, setHostName] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [busyId, setBusyId] = useState("");
    const isSuperAdmin = isSuperAdminUser();
    const [domainToDelete, setDomainToDelete] = useState(null);

    const primaryDomain = useMemo(() => {
        return domains.find((item) => item.isPrimary) || null;
    }, [domains]);

    const load = useCallback(async () => {
        if (!projectId) return;

        try {
            setLoading(true);
            const data = await getProjectDomains(projectId);
            setDomains(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error(error);
            toast.error("Failed to load custom domains.");
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        load();
    }, [load]);

    const refreshPanelOnly = async () => {
        await load();
    };

    const refreshProjectIfNeeded = async () => {
        if (onProjectChanged) {
            await onProjectChanged();
            return;
        }

        if (onChanged) {
            await onChanged();
        }
    };

    const handleAdd = async (event) => {
        event.preventDefault();

        const normalizedHost = normalizeHostInput(hostName);

        if (!normalizedHost) {
            toast.error("Enter a valid domain, for example www.clientdomain.com");
            return;
        }

        try {
            setSaving(true);

            await addProjectDomain(projectId, normalizedHost);

            toast.success("Domain added. Add DNS records, then mark it verified.");
            setHostName("");

            await refreshPanelOnly();

        } catch (error) {
            console.error(error);
            toast.error(
                error?.response?.data?.message ||
                error?.response?.data ||
                "Failed to add domain."
            );
        } finally {
            setSaving(false);
        }
    };

    const handleCheckDns = async (domain) => {
        try {
            setBusyId(domain.id);

            const result = await checkProjectDomainDns(projectId, domain.id);

            if (result?.status === "DnsVerified") {
                toast.success("DNS verified. Platform activation is now required.");
            } else {
                toast.error(result?.dnsMessage || result?.notes || "DNS records are not ready yet.");
            }

            await refreshPanelOnly();

        } catch (error) {
            console.error(error);
            toast.error(
                error?.response?.data?.message ||
                error?.response?.data ||
                "Failed to check DNS."
            );
        } finally {
            setBusyId("");
        }
    };

    const handleActivateAzure = async (domain, markActive = false) => {
        try {
            setBusyId(domain.id);

            await activateProjectDomainAzure(
                projectId,
                domain.id,
                markActive,
                markActive
                    ? "Azure binding and certificate confirmed by platform admin."
                    : "Azure activation queued by platform admin."
            );

            toast.success(markActive ? "Domain marked active." : "Azure activation marked pending.");

            await refreshPanelOnly();

        } catch (error) {
            console.error(error);
            toast.error(
                error?.response?.data?.message ||
                error?.response?.data ||
                "Failed to activate domain."
            );
        } finally {
            setBusyId("");
        }
    };

    const handleSetPrimary = async (domain) => {
        try {
            setBusyId(domain.id);

            await setProjectPrimaryDomain(projectId, domain.id);

            toast.success("Primary domain updated.");

            await refreshPanelOnly();
            await refreshProjectIfNeeded();

        } catch (error) {
            console.error(error);
            toast.error(
                error?.response?.data?.message ||
                error?.response?.data ||
                "Failed to set primary domain."
            );
        } finally {
            setBusyId("");
        }
    };

    const requestDeleteDomain = (domain) => {
        setDomainToDelete(domain);
    };

    const confirmDeleteDomain = async () => {
        if (!domainToDelete) return;

        const domain = domainToDelete;

        try {
            setBusyId(domain.id);

            await deleteProjectDomain(projectId, domain.id);

            toast.success("Domain removed successfully.");

            setDomainToDelete(null);

            await refreshPanelOnly();

            if (domain.isPrimary) {
                await refreshProjectIfNeeded();
            }
        } catch (error) {
            console.error(error);

            toast.error(
                error?.response?.data?.message ||
                error?.response?.data ||
                "Failed to delete domain."
            );
        } finally {
            setBusyId("");
        }
    };

    return (
        <section className={`project-panel project-domain-panel ${embedded ? "embedded" : ""}`}>
            {!embedded && (
                <div className="project-panel-header">
                    <div>
                        <span className="project-kicker">Custom Domains</span>
                        <h2>Connect client domain</h2>
                        <p>
                            Add a client domain, copy DNS instructions, verify it, and optionally
                            set it as the primary live URL for this project.
                        </p>
                    </div>

                    <button
                        type="button"
                        className="project-action-btn secondary"
                        onClick={load}
                        disabled={loading}
                    >
                        {loading ? "Refreshing..." : "Refresh Domains"}
                    </button>
                </div>
            )}

            {embedded && (
                <div className="project-domain-modal-actions">
                    <button
                        type="button"
                        className="project-action-btn secondary"
                        onClick={load}
                        disabled={loading}
                    >
                        {loading ? "Refreshing..." : "Refresh Domains"}
                    </button>
                </div>
            )}

            <div className="project-domain-current">
                <div>
                    <span>Current Live URL</span>
                    <strong>{primaryDomain?.hostName || liveUrl || "Not published yet"}</strong>
                    <small>
                        {primaryDomain
                            ? "Primary custom domain is active for this project."
                            : "Default generated-site URL is still active until an active custom domain is set as primary."}
                    </small>
                </div>

                {primaryDomain && (
                    <a
                        className="project-action-btn primary"
                        href={`https://${primaryDomain.hostName}/`}
                        target="_blank"
                        rel="noreferrer"
                    >
                        Open Domain
                    </a>
                )}
            </div>

            <form className="project-domain-add-form" onSubmit={handleAdd}>
                <label>
                    <span>Domain</span>
                    <input
                        value={hostName}
                        onChange={(event) => setHostName(event.target.value)}
                        placeholder="www.clientdomain.com"
                    />
                    <small>
                        Use a full host such as www.clientdomain.com. Root/apex domains can be added later after DNS/SSL planning.
                    </small>
                </label>

                <button
                    type="submit"
                    className="project-action-btn primary"
                    disabled={saving}
                >
                    {saving ? "Adding..." : "Add Domain"}
                </button>
            </form>

            {loading ? (
                <div className="project-loading-card">Loading domains...</div>
            ) : domains.length === 0 ? (
                <div className="project-empty">
                    No custom domains connected yet. Add a domain to generate DNS instructions.
                </div>
            ) : (
                <div className="project-domain-list">
                    {domains.map((domain) => {
                        const statusClass = getDomainStatusClass(domain.status);
                        const isBusy = busyId === domain.id;
                        const cnameHostName = getCommonDnsSubdomainName(domain.hostName);
                        const txtHostName = getCommonTxtHostName(domain.hostName);

                        return (
                            <article className="project-domain-card" key={domain.id}>
                                <div className="project-domain-card-head">
                                    <div>
                                        <h3>{domain.hostName}</h3>
                                        <p>
                                            Created {domain.createdAtUtc
                                                ? new Date(domain.createdAtUtc).toLocaleString()
                                                : "-"}
                                        </p>
                                    </div>

                                    <div className="project-domain-pills">
                                        <span className={`project-domain-status ${statusClass}`}>
                                            {formatDomainStatus(domain.status)}
                                        </span>

                                        {domain.isPrimary && (
                                            <span className="project-domain-status primary">
                                                Primary
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="project-domain-dns-table">
                                    <div className="project-domain-dns-row head">
                                        <span>Type</span>
                                        <span>Name / Host</span>
                                        <span>Value / Target</span>
                                        <span>Action</span>
                                    </div>

                                    <div className="project-domain-dns-row">
                                        <strong>TXT</strong>
                                        <div>
                                            <b>{txtHostName}</b>
                                            <small>Full: {domain.txtRecordName}</small>
                                        </div>
                                        <div>
                                            <b>{domain.txtRecordValue}</b>
                                            <small>Ownership verification token</small>
                                        </div>
                                        <div className="project-domain-copy-actions">
                                            <button
                                                type="button"
                                                className="project-copy-btn"
                                                onClick={() => copyText(txtHostName, "TXT host name")}
                                            >
                                                Copy Host
                                            </button>
                                            <button
                                                type="button"
                                                className="project-copy-btn"
                                                onClick={() => copyText(domain.txtRecordValue, "TXT value")}
                                            >
                                                Copy Value
                                            </button>
                                        </div>
                                    </div>

                                    <div className="project-domain-dns-row">
                                        <strong>CNAME</strong>
                                        <div>
                                            <b>{cnameHostName}</b>
                                            <small>Full: {domain.hostName}</small>
                                        </div>
                                        <div>
                                            <b>{domain.cnameTarget}</b>
                                            <small>Generated site host</small>
                                        </div>
                                        <div className="project-domain-copy-actions">
                                            <button
                                                type="button"
                                                className="project-copy-btn"
                                                onClick={() => copyText(cnameHostName, "CNAME host name")}
                                            >
                                                Copy Host
                                            </button>
                                            <button
                                                type="button"
                                                className="project-copy-btn"
                                                onClick={() => copyText(domain.cnameTarget, "CNAME target")}
                                            >
                                                Copy Target
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="project-domain-help">
                                    <strong>DNS setup</strong>
                                    <p>
                                        Add both records at your domain provider. In most DNS panels, use the short
                                        Name / Host value shown above because your root domain is added automatically.
                                        After saving TXT and CNAME, wait a few minutes and click Check DNS.
                                    </p>
                                </div>

                                <div className="project-domain-actions">
                                    {(domain.status === "PendingVerification" || domain.status === "Failed") && (
                                        <button
                                            type="button"
                                            className="project-action-btn compact secondary"
                                            disabled={isBusy}
                                            onClick={() => handleCheckDns(domain)}
                                        >
                                            {isBusy ? "Checking..." : "Check DNS"}
                                        </button>
                                    )}

                                    {isSuperAdmin && ["DnsVerified", "AzurePending", "Failed"].includes(domain.status) && (
                                        <button
                                            type="button"
                                            className="project-action-btn compact secondary"
                                            disabled={isBusy}
                                            onClick={() => handleActivateAzure(domain, false)}
                                        >
                                            {isBusy
                                                ? "Starting..."
                                                : domain.status === "AzurePending"
                                                    ? "Retry Azure Activation"
                                                    : domain.status === "Failed"
                                                        ? "Retry Azure Activation"
                                                        : "Start Azure Activation"}
                                        </button>
                                    )}

                                    {isSuperAdmin && ["DnsVerified", "AzurePending"].includes(domain.status) && (
                                        <button
                                            type="button"
                                            className="project-action-btn compact primary"
                                            disabled={isBusy}
                                            onClick={() => handleActivateAzure(domain, true)}
                                            title="Manual override only after Azure hostname binding and SSL certificate are confirmed."
                                        >
                                            {isBusy ? "Activating..." : "Mark Active"}
                                        </button>
                                    )}

                                    {domain.status === "Active" && !domain.isPrimary && (
                                        <button
                                            type="button"
                                            className="project-action-btn compact primary"
                                            disabled={isBusy}
                                            onClick={() => handleSetPrimary(domain)}
                                        >
                                            {isBusy ? "Setting..." : "Set Primary"}
                                        </button>
                                    )}

                                    <button
                                        type="button"
                                        className="project-action-btn compact danger"
                                        disabled={isBusy}
                                        onClick={() => requestDeleteDomain(domain)}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}

            {domainToDelete && (
                <div className="project-domain-confirm-backdrop">
                    <div className="project-domain-confirm-modal">
                        <span className="project-kicker">Remove Domain</span>

                        <h3>Remove {domainToDelete.hostName}?</h3>

                        <p>
                            Shriansh Nexus will remove the Azure hostname binding and managed
                            certificate first. If this domain is primary, the project will return
                            to the default generated-site URL.
                        </p>

                        <div className="project-domain-confirm-actions">
                            <button
                                type="button"
                                className="project-action-btn secondary"
                                onClick={() => setDomainToDelete(null)}
                                disabled={busyId === domainToDelete.id}
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                className="project-action-btn danger"
                                onClick={confirmDeleteDomain}
                                disabled={busyId === domainToDelete.id}
                            >
                                {busyId === domainToDelete.id ? "Removing..." : "Remove Domain"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}